import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CognitoIdentityProviderClient, ListUsersCommand, ListUsersInGroupCommand, AdminDisableUserCommand, AdminEnableUserCommand, AdminDeleteUserCommand, AdminAddUserToGroupCommand, AdminRemoveUserFromGroupCommand } from '@aws-sdk/client-cognito-identity-provider';

const USER_POOL_ID = 'eu-west-1_4DUdqPYom';
const REGION = 'eu-west-1';

function Admin() {
  const [gebruikers, setGebruikers] = useState([]);
  const [admins, setAdmins] = useState(new Set());
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState('');

  const getClient = async () => {
    const session = await fetchAuthSession();
    const credentials = session.credentials;
    return new CognitoIdentityProviderClient({ region: REGION, credentials });
  };

  const laadGebruikers = async () => {
    setLaden(true);
    setFout('');
    try {
      const client = await getClient();
      const [usersResult, adminsResult] = await Promise.all([
        client.send(new ListUsersCommand({ UserPoolId: USER_POOL_ID, Limit: 60 })),
        client.send(new ListUsersInGroupCommand({ UserPoolId: USER_POOL_ID, GroupName: 'admins', Limit: 60 }))
      ]);
      setGebruikers(usersResult.Users || []);
      setAdmins(new Set((adminsResult.Users || []).map(u => u.Username)));
    } catch (err) {
      setFout('Kan gebruikers niet laden: ' + err.message);
    } finally {
      setLaden(false);
    }
  };

  useEffect(() => { laadGebruikers(); }, []);

  const getAttr = (user, name) => user.Attributes?.find(a => a.Name === name)?.Value || '—';

  const handleToggle = async (user) => {
    try {
      const client = await getClient();
      if (user.Enabled) {
        await client.send(new AdminDisableUserCommand({ UserPoolId: USER_POOL_ID, Username: user.Username }));
      } else {
        await client.send(new AdminEnableUserCommand({ UserPoolId: USER_POOL_ID, Username: user.Username }));
      }
      laadGebruikers();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };

  const handleRolWijzigen = async (user) => {
    const isAdmin = admins.has(user.Username);
    if (!window.confirm(`Wil je ${getAttr(user, 'email')} ${isAdmin ? 'degraderen naar Standaard' : 'promoveren naar Admin'}?`)) return;
    try {
      const client = await getClient();
      if (isAdmin) {
        await client.send(new AdminRemoveUserFromGroupCommand({ UserPoolId: USER_POOL_ID, Username: user.Username, GroupName: 'admins' }));
      } else {
        await client.send(new AdminAddUserToGroupCommand({ UserPoolId: USER_POOL_ID, Username: user.Username, GroupName: 'admins' }));
      }
      laadGebruikers();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };

  const handleVerwijder = async (user) => {
    if (!window.confirm(`Weet je zeker dat je ${getAttr(user, 'email')} wilt verwijderen?`)) return;
    try {
      const client = await getClient();
      await client.send(new AdminDeleteUserCommand({ UserPoolId: USER_POOL_ID, Username: user.Username }));
      laadGebruikers();
    } catch (err) {
      alert('Fout: ' + err.message);
    }
  };

  const statusKleur = (user) => {
    if (!user.Enabled) return 'bg-red-100 text-red-800';
    if (user.UserStatus === 'CONFIRMED') return 'bg-green-100 text-green-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const statusLabel = (user) => {
    if (!user.Enabled) return 'Uitgeschakeld';
    if (user.UserStatus === 'CONFIRMED') return 'Actief';
    if (user.UserStatus === 'UNCONFIRMED') return 'Niet bevestigd';
    return user.UserStatus;
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Gebruikersbeheer</h2>
          <button onClick={laadGebruikers} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm">
            Vernieuwen
          </button>
        </div>

        {fout && <p className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm">{fout}</p>}

        {laden ? (
          <p className="text-gray-500 text-center py-8">Laden...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Gebruikersnaam', 'Voornaam', 'Achternaam', 'E-mail', 'Bedrijf', 'Functie', 'Rol', 'Status', 'Aangemeld op', 'Acties'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {gebruikers.map(user => (
                  <tr key={user.Username}>
                    <td className="px-4 py-3 text-sm text-gray-900">{getAttr(user, 'preferred_username')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAttr(user, 'given_name')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAttr(user, 'family_name')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAttr(user, 'email')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAttr(user, 'custom:bedrijf')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAttr(user, 'custom:functie')}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${admins.has(user.Username) ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                        {admins.has(user.Username) ? 'Admin' : 'Standaard'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusKleur(user)}`}>
                        {statusLabel(user)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(user.UserCreateDate).toLocaleDateString('nl-NL')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex space-x-2">
                        <button onClick={() => handleRolWijzigen(user)}
                          className={`px-2 py-1 rounded text-xs font-medium ${admins.has(user.Username) ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                          {admins.has(user.Username) ? '↓ Standaard' : '↑ Admin'}
                        </button>
                        <button onClick={() => handleToggle(user)}
                          className={`px-2 py-1 rounded text-xs font-medium ${user.Enabled ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}>
                          {user.Enabled ? 'Uitschakelen' : 'Inschakelen'}
                        </button>
                        <button onClick={() => handleVerwijder(user)}
                          className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200">
                          Verwijderen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-3">{gebruikers.length} gebruiker(s)</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
